import type { IAnalyticsEventFormField } from '@sravni/cosago-react-library/lib/types';
import { type FC, useCallback, useMemo } from 'react';

import { isSatisfyOption, useAppDispatch, useAppSelector } from 'shared/lib';
import {
  sendEventChangeFormFieldDefaultParam,
  sendEventFillFormField,
  SendEventWrapper,
} from 'shared/lib/sendGAEvents';
import { useUpdateStateBySelector } from 'shared/lib/useUpdateStateBySelector';

import { productTypeSelector } from 'entities/offersFilters';
import {
  type TCreditPurposeFormFields,
  CreditPurposeFormValidationSchema,
  selectCreditPurposeInfoData,
  formBlockFFSelector,
} from 'src/client/entities/creditForm';

import { submitCreditPurposeForm } from 'features/UpdateCreditPurposeInfo/lib/submitCreditPurposeForm';

import { FormFieldsControls } from '../../CreditPurposeInfo.config';
import { CreditCardPurposeInfoTexts, CreditPurposeInfoTexts } from '../../CreditPurposeInfo.texts';
import { CreditPurposeInfoViewHoc } from '../FormBody/FormBody';

export const Form: FC<
  IFormPopup & {
    shouldRestoreAdditionalData?: boolean;
  }
> = ({
  isLoading,
  isDialog = false,
  onClose,
  setHeader,
  isFormForceOpened,
  onFormSubmit,
  shouldRestoreAdditionalData,
  stepIndex,
  formId,
  submitButtonText = 'Далее',
  isAnyFormStepOpenedInPopup,
  analyticsOverrides,
}) => {
  const dispatch = useAppDispatch();
  const [formData] = useUpdateStateBySelector(selectCreditPurposeInfoData);
  const productType = useAppSelector(productTypeSelector);
  const formBlockFF = useAppSelector(formBlockFFSelector);

  const isCreditCard = formBlockFF?.isCreditCardsForm || productType === 'CreditCard';
  const formLabels = useMemo(
    () => (isCreditCard ? CreditCardPurposeInfoTexts : CreditPurposeInfoTexts),
    [isCreditCard],
  );

  const handleData = async (actualData: TCreditPurposeFormFields, type: string) => {
    if (type === 'fullFilled') {
      dispatch(submitCreditPurposeForm(actualData, analyticsOverrides));
      onFormSubmit?.(actualData, isDialog);
    }
    if (isDialog) {
      onClose?.(true);
    }
  };

  const sendAnalyticsEvent = useCallback(
    ({ fieldName }: IAnalyticsEventFormField) => {
      const isParameter = isSatisfyOption(['amount', 'period', 'purpose'], fieldName);
      if (isParameter) {
        sendEventChangeFormFieldDefaultParam(
          stepIndex + 1,
          CreditPurposeInfoTexts[fieldName as keyof TCreditPurposeFormFields],
          analyticsOverrides,
        );
      } else {
        sendEventFillFormField(
          stepIndex + 1,
          CreditPurposeInfoTexts[fieldName as keyof TCreditPurposeFormFields],
          true,
          analyticsOverrides,
        );
      }
    },
    [stepIndex, analyticsOverrides],
  );

  return (
    <SendEventWrapper sendEvent={sendAnalyticsEvent}>
      <CreditPurposeInfoViewHoc
        isDialog={isDialog}
        onDataChanged={handleData}
        defaultData={formData}
        validationSchema={CreditPurposeFormValidationSchema()}
        isLoading={isLoading}
        additionalProps={{
          setHeader,
          isFormForceOpened,
          shouldRestoreAdditionalData: !!shouldRestoreAdditionalData,
          formId,
          isAnyFormStepOpenedInPopup,
          analyticsOverrides,
        }}
        formLabels={formLabels}
        formFieldsControls={FormFieldsControls}
        submitButtonText={submitButtonText}
      />
    </SendEventWrapper>
  );
};
